import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { useI18n } from "../context/I18nContext";
import { api } from "../utils/api";

const NAV_ITEMS = [
  { id: "daily", labelKey: "daily", icon: "📅" },
  { id: "meals", labelKey: "meals", icon: "🍽️" },
  { id: "desserts", labelKey: "desserts", icon: "🍰" },
  { id: "ingredients", labelKey: "ingredientSuggest", icon: "🥕" },
  { id: "nutrition", labelKey: "nutrition", icon: "100g" },
  { id: "addRecipe", labelKey: "addRecipe", icon: "+" },
  { id: "favorites", labelKey: "favorites", icon: "♥" },
  { id: "shopping", labelKey: "shopping", icon: "🛒", badge: true },
  { id: "dietitian", labelKey: "dietitian", icon: "🥗" },
];

const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
let googleScriptPromise = null;

function loadGoogleIdentityScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("Tarayici ortami gerekli"));
  if (window.google?.accounts?.id) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;

  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GOOGLE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Google giris betigi yuklenemedi"));
    document.head.appendChild(script);
  });

  return googleScriptPromise;
}

export default function Navbar({ page, navigate }) {
  const { state, actions } = useApp();
  const { language, languages, setLanguage, t } = useI18n();
  const [showLogin, setShowLogin] = useState(false);
  const navIcons = state.settings?.navIcons || {};
  const profileIcon = String(navIcons.profile || "").trim().toLocaleLowerCase("tr-TR") === "profil"
    ? "👤"
    : navIcons.profile || "👤";
  const brand = state.settings?.brandName || t("brand");
  const brandWordmark = brand === "FitDish"
    ? <><span className="brand-fit">Fit</span><span className="brand-dish">Dish</span></>
    : brand;
  const goToPage = (pageId) => navigate(pageId);

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <button className="nav-logo" onClick={() => goToPage("daily")}>{brandWordmark}</button>
        <div className="nav-links">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              className={`nav-btn ${page === item.id ? "active" : ""}`}
              onClick={() => goToPage(item.id)}
            >
              <span>{navIcons[item.id] || item.icon}</span>
              <span>{t(item.labelKey)}</span>
              {item.badge && state.shoppingCount > 0 && <span className="nav-badge">{state.shoppingCount}</span>}
            </button>
          ))}
          {state.user && (
            <button
              className={`nav-btn ${page === "profile" ? "active" : ""}`}
              onClick={() => goToPage("profile")}
            >
              <span>{profileIcon}</span>
              <span>{t("myProfile")}</span>
            </button>
          )}
          {state.user?.isAdmin && (
            <button
              className={`nav-btn ${page === "admin" ? "active" : ""}`}
              onClick={() => goToPage("admin")}
            >
              <span>{navIcons.admin || "Admin"}</span>
              <span>{t("admin")}</span>
            </button>
          )}
        </div>
        <div className="nav-tools">
          <select value={language} onChange={(event) => setLanguage(event.target.value)} className="language-select" title={t("language")}>
            {languages.map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}
          </select>
          {state.user ? (
            <button className="btn btn-ghost btn-sm" onClick={actions.logout}>{state.user.name} - {t("signOut")}</button>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => setShowLogin(true)}>{t("signIn")}</button>
          )}
        </div>
      </div>
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </nav>
  );
}

function LoginModal({ onClose }) {
  const { actions } = useApp();
  const { t } = useI18n();
  const googleButtonRef = useRef(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [adminCode, setAdminCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const showDevLogin = process.env.NODE_ENV !== "production";

  useEffect(() => {
    let cancelled = false;

    const setupGoogleButton = async () => {
      try {
        setError("");
        const envClientId = String(process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || "").trim();
        const response = envClientId ? null : await api.getGoogleClientId();
        const clientId = envClientId || String(response?.data?.clientId || "").trim();
        if (!clientId) throw new Error("Google Web Client ID bulunamadi");

        await loadGoogleIdentityScript();
        if (cancelled || !googleButtonRef.current || !window.google?.accounts?.id) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            if (!response?.credential) {
              setError("Google girisi icin token alinamadi");
              return;
            }
            setLoading(true);
            setError("");
            try {
              await actions.socialLogin({ provider: "google", idToken: response.credential });
              onClose();
            } catch (err) {
              setError(err.message || "Google girisi tamamlanamadi");
            } finally {
              if (!cancelled) setLoading(false);
            }
          }
        });

        googleButtonRef.current.innerHTML = "";
        const width = Math.min(360, googleButtonRef.current.clientWidth || 320);
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          width
        });
        if (!cancelled) setGoogleReady(true);
      } catch (err) {
        if (!cancelled) setError(err.message || "Google girisi hazirlanamadi");
      }
    };

    setupGoogleButton();

    return () => {
      cancelled = true;
    };
  }, [actions, onClose]);

  const login = async (provider) => {
    setLoading(true);
    setError("");
    try {
      const cleanEmail = email.trim() || `demo@${provider.toLowerCase()}.example`;
      await actions.socialLogin({
        provider: provider.toLowerCase(),
        providerUserId: cleanEmail,
        name: name.trim() || `${provider} User`,
        email: cleanEmail
      });
      onClose();
    } catch (err) {
      setError(err.message || "Giris tamamlanamadi");
    } finally {
      setLoading(false);
    }
  };

  const adminLogin = async () => {
    setLoading(true);
    setError("");
    try {
      await actions.adminCodeLogin({
        email: email.trim(),
        code: adminCode.trim()
      });
      onClose();
    } catch (err) {
      const detail = [err.code, err.status].filter(Boolean).join(" / ");
      setError(`${err.message || "Admin girisi tamamlanamadi"}${detail ? ` (${detail})` : ""}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <div className="modal-header">
          <h3>{t("signIn")}</h3>
          <button onClick={onClose}>x</button>
        </div>
        <p className="muted">{t("socialInfo")}</p>
        <div className="google-signin-slot" aria-busy={!googleReady || loading}>
          <div ref={googleButtonRef} />
          {!googleReady && !error && <span className="muted">Google hazirlaniyor...</span>}
        </div>
        {error && <p className="login-error">{error}</p>}
        <div className="login-fallback-separator">Admin kodu</div>
        <div className="login-fields">
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t("emailAddress")}
            disabled={loading}
          />
          <input
            type="password"
            value={adminCode}
            onChange={(event) => setAdminCode(event.target.value)}
            placeholder="Admin giris kodu"
            disabled={loading}
          />
        </div>
        <div className="social-buttons">
          <button className="btn btn-outline" onClick={adminLogin} disabled={loading || !email.trim() || !adminCode.trim()}>
            Admin paneline gir
          </button>
        </div>
        <a className="admin-login-link" href="/admin-login">
          Alternatif admin giriş sayfasını aç
        </a>
        {showDevLogin && (
          <>
            <div className="login-fallback-separator">Gelistirme girisi</div>
            <div className="login-fields">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t("emailAddress")}
                disabled={loading}
              />
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t("displayName")}
                disabled={loading}
              />
            </div>
            <div className="social-buttons">
              <button className="btn btn-outline" onClick={() => login("Google")} disabled={loading}>
                {t("continueWith")} Google Demo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
